// Temporary EXR mock for the SHARED admin season endpoints (create/get/update/settings/save-population) (GMTEC-23994)
// that flow through GeneralSeasonService & SeasonSettingsService and can't be swapped per season type.
// Everything EXR-specific should be a traditional mock instead (see exr-season-store.ts).
// TODO: Replace with the real EXR backend; delete this together with exr-season-store.ts. (GMTEC-23994)
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FeatureFlagsService } from '@coin/shared/core/data-access';
import { SeasonProcessState, SeasonTypeEnum, TransactionState } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, filter, map, switchMap } from 'rxjs/operators';
import {
  createExrSeason,
  getExrSeason,
  getExrSeasonSettings,
  saveExrSeasonSettings,
  updateExrSeason
} from './exr-season-store';
import { regenerateExrApprovalChains } from './exr-approval-chain-store';
import {
  applyExrMassApproval,
  deleteExrRecords,
  exportExrRecords,
  ExrMassApprovalOutcome,
  ExrMassDeleteOutcome,
  ExrPayrollExportOutcome,
  getExrRecordsByIds,
  hasExrRecords,
  seedExrRecords
} from './exr-record-store';
import { listExrPlans } from './exr-plan-store';
import { getExrMonitoringMissingRequirements } from './exr-monitoring-validation';

@Injectable()
export class ExrMockInterceptor implements HttpInterceptor {
  private readonly featureFlags = inject(FeatureFlagsService);

  private static readonly TX_PREFIX = 'exr-mock-tx-';
  private static readonly TX_RESULTS_KEY = 'exr-mock-tx-results';

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.featureFlags.isActive('exceptionalRecognitions')) {
      return next.handle(req);
    }

    const path = new URL(req.url, window.location.origin).pathname;
    const method = req.method.toUpperCase();
    const body = req.body as Record<string, unknown>;

    // Only the mock's own transaction ids — every other transaction-status request passes through.
    const txMatch = path.match(/\/transaction-status\/([^/]+)$/);
    if (method === 'GET' && txMatch?.[1].startsWith(ExrMockInterceptor.TX_PREFIX)) {
      return this.respond(this.finishedTransaction(txMatch[1]));
    }

    // EXR monitoring Board/CFO mass-approval ops. (GMTEC-20710)
    const massApprovalMatch = path.match(
      /\/seasons\/([^/]+)\/records\/exceptional-recognition\/approvals\/set-(board|cfo)$/
    );
    if (method === 'POST' && massApprovalMatch) {
      return this.respondToMassApproval(massApprovalMatch[1], massApprovalMatch[2] === 'board' ? 'board' : 'cfo', req);
    }

    // EXR monitoring approval chain regeneration. (GMTEC-20707)
    const regenerateMatch = path.match(/\/seasons\/([^/]+)\/records\/exceptional-recognition\/approvals\/regenerate$/);
    if (method === 'PUT' && regenerateMatch) {
      return this.respondToChainRegeneration(regenerateMatch[1], req);
    }

    // EXR monitoring mass delete. (GMTEC-20711)
    const massDeleteMatch = path.match(/\/seasons\/([^/]+)\/records\/exceptional-recognition\/delete$/);
    if (method === 'POST' && massDeleteMatch) {
      return this.respondToMassDelete(massDeleteMatch[1], req);
    }

    // EXR monitoring payroll export (test / final). (GMTEC-20709)
    const payrollExportMatch = path.match(
      /\/seasons\/([^/]+)\/records\/exceptional-recognition\/payroll-export\/(test|final)$/
    );
    if (method === 'POST' && payrollExportMatch) {
      return this.respondToPayrollExport(
        payrollExportMatch[1],
        payrollExportMatch[2] === 'test' ? 'test' : 'final',
        req
      );
    }

    const settingsByIdMatch = path.match(/\/exceptional-recognition\/settings\/seasons\/([^/]+)$/);
    if (method === 'GET' && settingsByIdMatch) {
      return this.respond(getExrSeasonSettings(settingsByIdMatch[1]));
    }
    if (method === 'PUT' && /\/exceptional-recognition\/settings$/.test(path)) {
      return this.respond(saveExrSeasonSettings(body));
    }

    // The seasons LIST is a traditional mock (ExceptionalRecognitionSeasonService) and never hits the network.
    if (path.includes('/exceptional-recognition/seasons')) {
      const savePopMatch = path.match(/\/seasons\/([^/]+)\/save-population$/);
      if (method === 'POST' && savePopMatch) {
        return this.respond(this.startedTransaction(savePopMatch[1]));
      }
      const updateStateMatch = path.match(/\/exceptional-recognition\/seasons\/([^/]+)\/update-state$/);
      if (method === 'PATCH' && updateStateMatch) {
        return this.respondToStateTransition(updateStateMatch[1], body?.['state'] as SeasonProcessState);
      }
      const byIdMatch = path.match(/\/exceptional-recognition\/seasons\/([^/]+)$/);
      if (method === 'GET' && byIdMatch) {
        return this.respond(getExrSeason(byIdMatch[1]));
      }
      if (method === 'POST' && /\/exceptional-recognition\/seasons$/.test(path)) {
        return this.respondToSeasonCreation(req, next);
      }
    }

    // updateSeason() PUTs every type to /merit/seasons, so match by body to catch only EXR ones.
    if (
      method === 'PUT' &&
      /\/merit\/seasons$/.test(path) &&
      body?.['seasonType'] === SeasonTypeEnum.ExceptionalRecognitions
    ) {
      return this.respond(updateExrSeason(body));
    }

    return next.handle(req);
  }

  // The dialog sends only the assignee's id; like the real backend, the mock resolves it to the user so the overview can
  // show the name. Goes through the downstream handler, as injecting HttpClient here would be circular. (GMTEC-20685)
  private respondToSeasonCreation(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const body = req.body as Record<string, unknown>;
    const assignedToId = body['assignedTo'] as string | undefined;
    const assignee$ = assignedToId
      ? next
          .handle(
            new HttpRequest('GET', `${environment.api.baseUrl}/admin/api/v2/master/users/${assignedToId}`, {
              headers: req.headers
            })
          )
          .pipe(
            filter(event => event instanceof HttpResponse),
            map(response => response.body as Record<string, string>),
            catchError(() => of(null))
          )
      : of(null);
    return assignee$.pipe(
      switchMap(assignedTo => this.respond(createExrSeason({ ...body, assignedToId, assignedTo })))
    );
  }

  // The real backend validates Monitoring-readiness inside the transition endpoint and rejects with the unmet
  // requirements; the mock mirrors that here. Other transitions just persist the new state.
  private respondToStateTransition(seasonId: string, newState: SeasonProcessState): Observable<HttpEvent<unknown>> {
    if (newState === SeasonProcessState.Monitoring) {
      const missingRequirements = getExrMonitoringMissingRequirements(
        getExrSeasonSettings(seasonId),
        listExrPlans(seasonId).content
      );
      if (missingRequirements.length) {
        return this.reject({ missingRequirements });
      }
      seedExrRecords(seasonId);
    }
    // Plans may only change again while nobody has proposed yet. (GMTEC-20689)
    if (
      newState === SeasonProcessState.PlanManagement &&
      getExrSeason(seasonId)?.state === SeasonProcessState.Monitoring &&
      hasExrRecords(seasonId)
    ) {
      return this.reject({ message: 'The season can only return to Plan Management while no proposals exist.' });
    }
    return this.respond(updateExrSeason({ id: seasonId, state: newState }));
  }

  // Applies the mass-approval, stores the per-item result under a fresh transaction id, and returns a started
  // transaction. The client polls /transaction-status/{id}, which finishedTransaction() resolves with that result.
  private respondToMassApproval(
    seasonId: string,
    flag: 'board' | 'cfo',
    req: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    const filterText = new URL(req.url, window.location.origin).search;
    const recordIds = (req.body as { recordIds?: string[] })?.recordIds;

    const outcome = applyExrMassApproval(seasonId, { recordIds, filterText }, flag, this.cfoThreshold(seasonId));
    const transaction = this.startedTransaction(seasonId);
    this.storeTxResult(String(transaction.transactionId), this.buildMassApprovalMetadata(outcome));
    return this.respond(transaction);
  }

  // Deletes the selection and stores the per-item result under a fresh transaction id, like the mass-approval op. (GMTEC-20711)
  private respondToMassDelete(seasonId: string, req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    const filterText = new URL(req.url, window.location.origin).search;
    const recordIds = (req.body as { recordIds?: string[] })?.recordIds;

    const outcome = deleteExrRecords(seasonId, { recordIds, filterText }, this.cfoThreshold(seasonId));
    const transaction = this.startedTransaction(seasonId);
    this.storeTxResult(String(transaction.transactionId), this.buildMassDeleteMetadata(outcome));
    return this.respond(transaction);
  }

  // Runs the payroll export for the selection and stores the per-item result (with the generated file URI) under a
  // fresh transaction id, like the other mass ops. (GMTEC-20709)
  private respondToPayrollExport(
    seasonId: string,
    mode: 'test' | 'final',
    req: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    const filterText = new URL(req.url, window.location.origin).search;
    const recordIds = (req.body as { recordIds?: string[] })?.recordIds;

    const outcome = exportExrRecords(seasonId, { recordIds, filterText }, mode, this.cfoThreshold(seasonId));
    const transaction = this.startedTransaction(seasonId);
    this.storeTxResult(String(transaction.transactionId), this.buildPayrollExportMetadata(outcome));
    return this.respond(transaction);
  }

  // Recalculates the open approval steps of the given proposals. Unlike the mass operations this answers synchronously
  // with a StatusResponse instead of a transaction, mirroring the special-payment regenerate endpoint. (GMTEC-20707)
  private respondToChainRegeneration(seasonId: string, req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    const recordIds = (req.body as { recordIds?: string[] })?.recordIds ?? [];
    const records = getExrRecordsByIds(seasonId, recordIds);
    if (!records.length) {
      return this.respond({ isSuccess: false, message: 'The proposal no longer exists' });
    }
    return this.respond(regenerateExrApprovalChains(seasonId, records, getExrSeasonSettings(seasonId)));
  }

  // Unset threshold (0) means every Transformation Incentive proposal needs the CFO flag.
  private cfoThreshold(seasonId: string): number {
    const settings = getExrSeasonSettings(seasonId) as Record<string, unknown> | null;
    return Number(settings?.['cfoApprovalThresholdAmount'] ?? 0);
  }

  // Skipped items surface as warnings (per-item reason), so RequestSummaryComponent lists them with the modified count.
  private buildMassApprovalMetadata(outcome: ExrMassApprovalOutcome): Record<string, unknown> {
    return {
      successCount: outcome.modifiedIds.length,
      failureCount: 0,
      warningsCount: outcome.skipped.length,
      totalCount: outcome.modifiedIds.length + outcome.skipped.length,
      // Objects, not raw ids: RequestSummaryComponent renders each entry via Object.keys(), so a bare string prints per-character.
      modifiedEntities: outcome.modifiedIds.map(recordId => ({ recordId })),
      downloadUri: '',
      failures: [],
      warnings: outcome.skipped
    };
  }

  // Protected proposals surface as failures (per-item reason), so RequestSummaryComponent lists them under the
  // failure count next to the deleted records. (GMTEC-20711)
  private buildMassDeleteMetadata(outcome: ExrMassDeleteOutcome): Record<string, unknown> {
    return {
      successCount: outcome.deletedIds.length,
      failureCount: outcome.failed.length,
      warningsCount: 0,
      totalCount: outcome.deletedIds.length + outcome.failed.length,
      modifiedEntities: outcome.deletedIds.map(recordId => ({ recordId })),
      downloadUri: '',
      failures: outcome.failed,
      warnings: []
    };
  }

  // Exported records surface as successes; the approved-but-blocked ones (missing Board/CFO flag, unmapped Sign-On
  // Bonus) surface as warnings. Records outside the export set are only counted, so the total still reconciles with the
  // selection. The generated file travels on downloadUri and is downloaded by the component. (GMTEC-20709)
  private buildPayrollExportMetadata(outcome: ExrPayrollExportOutcome): Record<string, unknown> {
    return {
      successCount: outcome.exportedIds.length,
      failureCount: 0,
      warningsCount: outcome.blocked.length,
      skippedCount: outcome.skippedCount,
      totalCount: outcome.exportedIds.length + outcome.blocked.length + outcome.skippedCount,
      modifiedEntities: outcome.exportedIds.map(recordId => ({ recordId })),
      downloadUri: outcome.downloadUri,
      fileName: outcome.fileName,
      failures: [],
      warnings: outcome.blocked
    };
  }

  private storeTxResult(transactionId: string, metadata: Record<string, unknown>): void {
    const results = this.readTxResults();
    results[transactionId] = metadata;
    sessionStorage.setItem(ExrMockInterceptor.TX_RESULTS_KEY, JSON.stringify(results));
  }

  private readTxResults(): Record<string, Record<string, unknown>> {
    try {
      return JSON.parse(sessionStorage.getItem(ExrMockInterceptor.TX_RESULTS_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  private startedTransaction(entityId: string): Record<string, unknown> {
    const now = new Date().toISOString();
    return {
      transactionId: `${ExrMockInterceptor.TX_PREFIX}${crypto.randomUUID()}`,
      entityId,
      startedAt: now,
      updatedAt: now,
      finishedAt: now,
      state: TransactionState.Started,
      progressCount: 0,
      totalCount: 1,
      processTitle: 'EXR mock',
      metadata: {}
    };
  }

  private finishedTransaction(transactionId: string): Record<string, unknown> {
    const now = new Date().toISOString();
    // Mass-approval txns carry a stored per-item result; other txns (e.g. save-population) fall back to a plain success.
    const metadata = this.readTxResults()[transactionId] ?? {
      successCount: 1,
      failureCount: 0,
      warningsCount: 0,
      totalCount: 1,
      modifiedEntities: [],
      downloadUri: '',
      failures: [],
      warnings: []
    };
    const totalCount = Number(metadata['totalCount'] ?? 1);
    return {
      transactionId,
      entityId: '',
      startedAt: now,
      updatedAt: now,
      finishedAt: now,
      state: TransactionState.Finished,
      progressCount: totalCount,
      totalCount,
      processTitle: 'EXR mock',
      metadata
    };
  }

  private reject(error: Record<string, unknown>): Observable<never> {
    return throwError(() => new HttpErrorResponse({ status: 400, error })).pipe(delay(150));
  }

  private respond<T>(responseBody: T): Observable<HttpEvent<T>> {
    return of(new HttpResponse({ status: 200, body: responseBody })).pipe(delay(150));
  }
}
